import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { BarChart3, Trophy, Calendar, CheckCircle, MessageCircle, Lock, Twitter, Instagram, Youtube, Facebook, Send, User } from 'lucide-react';
import { toast } from 'sonner';
import SocialLoginButtons from '../components/SocialLoginButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FanZonePage = () => {
  const { user, login } = useContext(AuthContext);
  const [polls, setPolls] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [votedPolls, setVotedPolls] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('polls');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pollsRes, predictionsRes] = await Promise.all([
          axios.get(`${API}/polls`),
          axios.get(`${API}/predictions`)
        ]);
        setPolls(pollsRes.data);
        setPredictions(predictionsRes.data);
        
        const voted = JSON.parse(localStorage.getItem('votedPolls') || '{}');
        setVotedPolls(voted);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleVote = async (pollId, option) => {
    if (votedPolls[pollId]) {
      toast.info('You already voted in this poll!');
      return;
    }

    try {
      const response = await axios.post(`${API}/polls/vote`, {
        poll_id: pollId,
        option: option
      });

      setPolls(polls.map(p => p.poll_id === pollId ? response.data : p));
      
      const newVoted = { ...votedPolls, [pollId]: option };
      setVotedPolls(newVoted);
      localStorage.setItem('votedPolls', JSON.stringify(newVoted));
      
      toast.success('Vote recorded!');
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to record vote');
    }
  };

  const handlePick = async (predictionId, team) => {
    if (!user) {
      toast.error('Please login to make predictions');
      login();
      return;
    }

    try {
      const response = await axios.post(
        `${API}/predictions/pick`,
        { prediction_id: predictionId, team },
        { withCredentials: true }
      );
      setPredictions(predictions.map(p => p.prediction_id === predictionId ? response.data : p));
      toast.success('Pick recorded!');
    } catch (error) {
      console.error('Error making pick:', error);
      toast.error('Failed to record pick');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fan-zone-page">
      <div className="bg-[#001f3f] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase heading-font" data-testid="fan-zone-title">
            Fan Zone
          </h1>
          <p className="text-xl text-slate-300 mt-4">Vote in polls, make predictions, and join the conversation!</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('polls')}
              className={`flex items-center gap-2 px-5 py-4 font-bold uppercase tracking-wider text-sm border-b-4 transition-colors whitespace-nowrap ${
                activeTab === 'polls'
                  ? 'border-[#DC2626] text-[#DC2626]'
                  : 'border-transparent text-slate-600 hover:text-[#001f3f]'
              }`}
              data-testid="polls-tab"
            >
              <BarChart3 className="h-5 w-5" />
              Polls
            </button>
            <button
              onClick={() => setActiveTab('predictions')}
              className={`flex items-center gap-2 px-5 py-4 font-bold uppercase tracking-wider text-sm border-b-4 transition-colors whitespace-nowrap ${
                activeTab === 'predictions'
                  ? 'border-[#DC2626] text-[#DC2626]'
                  : 'border-transparent text-slate-600 hover:text-[#001f3f]'
              }`}
              data-testid="predictions-tab"
            >
              <Trophy className="h-5 w-5" />
              Predictions
            </button>
            <button
              onClick={() => setActiveTab('swac-talk')}
              className={`flex items-center gap-2 px-5 py-4 font-bold uppercase tracking-wider text-sm border-b-4 transition-colors whitespace-nowrap ${
                activeTab === 'swac-talk'
                  ? 'border-[#DC2626] text-[#DC2626]'
                  : 'border-transparent text-slate-600 hover:text-[#001f3f]'
              }`}
              data-testid="swac-talk-tab"
            >
              <MessageCircle className="h-5 w-5" />
              SWAC Talk
            </button>
            <button
              onClick={() => setActiveTab('members')}
              className={`flex items-center gap-2 px-5 py-4 font-bold uppercase tracking-wider text-sm border-b-4 transition-colors whitespace-nowrap ${
                activeTab === 'members'
                  ? 'border-[#DC2626] text-[#DC2626]'
                  : 'border-transparent text-slate-600 hover:text-[#001f3f]'
              }`}
              data-testid="members-tab"
            >
              <Lock className="h-5 w-5" />
              Members Only
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading && activeTab !== 'swac-talk' && activeTab !== 'members' ? (
          <div className="text-center py-12" data-testid="loading">Loading...</div>
        ) : activeTab === 'polls' ? (
          /* Polls Section */
          polls.length === 0 ? (
            <div className="text-center py-12 text-slate-600" data-testid="no-polls">
              No active polls right now. Check back soon!
            </div>
          ) : (
            <div className="space-y-8">
              {polls.map(poll => (
                <PollCard 
                  key={poll.poll_id} 
                  poll={poll} 
                  hasVoted={!!votedPolls[poll.poll_id]}
                  userVote={votedPolls[poll.poll_id]}
                  onVote={handleVote}
                />
              ))}
            </div>
          )
        ) : activeTab === 'predictions' ? (
          /* Predictions Section */
          predictions.length === 0 ? (
            <div className="text-center py-12 text-slate-600" data-testid="no-predictions">
              No active predictions right now. Check back soon!
            </div>
          ) : (
            <div className="space-y-6">
              {predictions.map(prediction => (
                <PredictionCard 
                  key={prediction.prediction_id} 
                  prediction={prediction} 
                  user={user}
                  onPick={handlePick}
                />
              ))}
            </div>
          )
        ) : activeTab === 'swac-talk' ? (
          /* SWAC Talk Section */
          <SWACTalkSection />
        ) : (
          /* Members Only Section */
          <MembersOnlySection />
        )}
      </div>
    </div>
  );
};

const SWACTalkSection = () => {
  const { user, login, requiresNickname, promptNickname } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Current topic of the day
  const topicOfTheDay = "Should Quarterbacks Be Able to Play More than 6 Seasons?";

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/forum/swac-talk`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      // Start with empty if no messages yet
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMessage = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to join the conversation');
      login();
      return;
    }
    
    // Check if user needs to set nickname first
    if (requiresNickname()) {
      promptNickname();
      toast.info('Please set a nickname before posting');
      return;
    }
    
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/forum/swac-talk`, 
        {
          content: newMessage,
          user_id: user.user_id,
          user_name: user.nickname || user.name,
          user_picture: user.picture
        },
        { withCredentials: true }
      );
      setMessages([response.data, ...messages]);
      setNewMessage('');
      toast.success('Message posted!');
    } catch (error) {
      console.error('Error posting message:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to post message';
      if (errorMsg.includes('nickname')) {
        promptNickname();
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="swac-talk-section">
      {/* Topic of the Day */}
      <div className="bg-gradient-to-r from-[#DC2626] to-[#991b1b] rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center space-x-3 mb-3">
          <MessageCircle className="h-8 w-8" />
          <h2 className="text-2xl font-bold uppercase tracking-wider heading-font">
            SheLovesThee SWAC Talk
          </h2>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-lg p-4">
          <p className="text-sm uppercase tracking-wider opacity-80 mb-1">Topic of the Day</p>
          <p className="text-xl md:text-2xl font-bold">{topicOfTheDay}</p>
        </div>
      </div>

      {/* Message Input */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {user ? (
          <form onSubmit={handleSubmitMessage} className="space-y-4">
            <div className="flex items-start space-x-4">
              {user.picture ? (
                <img src={user.picture} alt={user.nickname || user.name} className="w-12 h-12 rounded-full" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#001f3f] flex items-center justify-center">
                  <User className="h-6 w-6 text-white" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <p className="font-bold text-[#001f3f]">{user.nickname || user.name}</p>
                  {!user.nickname && (
                    <button
                      type="button"
                      onClick={() => promptNickname()}
                      className="text-xs bg-[#DC2626] text-white px-2 py-1 rounded-full hover:bg-[#b91c1c] transition-colors"
                      data-testid="set-nickname-btn"
                    >
                      Set Nickname
                    </button>
                  )}
                </div>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={user.nickname ? "Drop your hot take on the topic..." : "Set a nickname to join the conversation..."}
                  className="w-full p-4 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-[#DC2626] focus:border-transparent resize-none"
                  rows="3"
                  data-testid="swac-talk-input"
                  disabled={!user.nickname}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || !newMessage.trim() || !user.nickname}
                className="flex items-center gap-2 bg-[#DC2626] hover:bg-[#b91c1c] text-white px-6 py-3 rounded-full font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="swac-talk-submit"
              >
                <Send className="h-5 w-5" />
                {submitting ? 'Posting...' : 'Post Message'}
              </button>
            </div>
          </form>
        ) : (
          <div className="text-center py-8">
            <MessageCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 mb-4 text-lg">Login to join the SWAC Talk!</p>
            <p className="text-slate-500 mb-6">Share your hot takes, debate the topics, and connect with other fans.</p>
            <SocialLoginButtons />
          </div>
        )}
      </div>

      {/* Rolling Messages */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-[#001f3f] text-white p-4">
          <h3 className="font-bold uppercase tracking-wider flex items-center">
            <MessageCircle className="h-5 w-5 mr-2" />
            Live Discussion
          </h3>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-semibold">No messages yet!</p>
              <p className="text-sm">Be the first to share your thoughts on today's topic.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {messages.map((msg, idx) => (
                <div key={msg.message_id || idx} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start space-x-3">
                    {msg.user_picture ? (
                      <img src={msg.user_picture} alt={msg.user_name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <User className="h-5 w-5 text-slate-500" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-bold text-[#001f3f]">{msg.user_name}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(msg.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-slate-700">{msg.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MembersOnlySection = () => {
  return (
    <div className="text-center py-16" data-testid="members-only-section">
      <div className="bg-gradient-to-br from-[#001f3f] to-[#003366] rounded-2xl p-12 shadow-2xl max-w-2xl mx-auto">
        <div className="bg-[#DC2626] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
          <Lock className="h-10 w-10 text-white" />
        </div>
        
        <h2 className="text-4xl md:text-5xl font-black uppercase heading-font text-white mb-4">
          Members Only
        </h2>
        
        <div className="bg-[#DC2626] text-white px-6 py-3 rounded-full inline-block mb-6">
          <span className="text-xl font-bold uppercase tracking-wider">Coming Soon!</span>
        </div>
        
        <p className="text-3xl md:text-4xl font-bold text-[#DC2626] mb-4">
          Late Summer 2026
        </p>
        
        <p className="text-xl text-slate-300 mb-8">
          Exclusive content, behind-the-scenes access, and premium features for our dedicated fans!
        </p>
        
        <div className="bg-white/10 backdrop-blur rounded-xl p-6 mb-8">
          <p className="text-2xl md:text-3xl font-black uppercase text-white">
            Join or Be Left Out!
          </p>
        </div>
        
        <div className="space-y-3 text-left max-w-md mx-auto">
          <div className="flex items-center space-x-3 text-slate-300">
            <div className="w-2 h-2 bg-[#DC2626] rounded-full"></div>
            <span>Exclusive interviews & content</span>
          </div>
          <div className="flex items-center space-x-3 text-slate-300">
            <div className="w-2 h-2 bg-[#DC2626] rounded-full"></div>
            <span>Early access to predictions & picks</span>
          </div>
          <div className="flex items-center space-x-3 text-slate-300">
            <div className="w-2 h-2 bg-[#DC2626] rounded-full"></div>
            <span>Ad-free experience</span>
          </div>
          <div className="flex items-center space-x-3 text-slate-300">
            <div className="w-2 h-2 bg-[#DC2626] rounded-full"></div>
            <span>Member-only Discord access</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PollCard = ({ poll, hasVoted, userVote, onVote }) => {
  const totalVotes = Object.values(poll.votes).reduce((sum, count) => sum + count, 0);

  const getPercentage = (option) => {
    if (totalVotes === 0) return 0;
    return Math.round((poll.votes[option] / totalVotes) * 100);
  };

  return (
    <div className="bg-white shadow-lg p-8 rounded-lg" data-testid={`poll-${poll.poll_id}`}>
      <div className="flex items-start justify-between mb-6">
        <h3 className="text-2xl font-bold text-[#001f3f] heading-font">{poll.question}</h3>
        <BarChart3 className="h-6 w-6 text-[#007AFF]" />
      </div>

      <div className="space-y-3">
        {poll.options.map(option => (
          <div key={option} className="relative">
            {hasVoted ? (
              <div className="relative overflow-hidden border-2 border-slate-200 rounded p-4">
                <div 
                  className="absolute left-0 top-0 bottom-0 bg-blue-100"
                  style={{ width: `${getPercentage(option)}%` }}
                ></div>
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{option}</span>
                    {userVote === option && (
                      <CheckCircle className="h-5 w-5 text-[#007AFF]" />
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="font-bold text-[#007AFF]">{getPercentage(option)}%</span>
                    <span className="text-sm text-slate-600">({poll.votes[option]} votes)</span>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => onVote(poll.poll_id, option)}
                className="w-full text-left p-4 border-2 border-slate-200 rounded hover:border-[#007AFF] hover:bg-slate-50 transition-all duration-200 font-medium"
              >
                {option}
              </button>
            )}
          </div>
        ))}
      </div>

      {hasVoted && (
        <div className="mt-4 text-sm text-slate-600 text-center">
          Total votes: {totalVotes}
        </div>
      )}
    </div>
  );
};

const PredictionCard = ({ prediction, user, onPick }) => {
  const userPick = user ? prediction.user_picks[user.user_id] : null;
  const totalPicks = Object.keys(prediction.user_picks).length;
  const team1Picks = Object.values(prediction.user_picks).filter(p => p === prediction.team1).length;
  const team2Picks = Object.values(prediction.user_picks).filter(p => p === prediction.team2).length;

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      <div className="bg-[#001f3f] text-white p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold uppercase heading-font">{prediction.game_title}</h3>
          <div className="flex items-center space-x-2 text-sm">
            <Calendar className="h-4 w-4" />
            <span>{new Date(prediction.game_date).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-3 gap-4 items-center">
          <TeamOption 
            team={prediction.team1}
            logo={prediction.team1_logo}
            isPicked={userPick === prediction.team1}
            pickCount={team1Picks}
            totalPicks={totalPicks}
            onPick={() => onPick(prediction.prediction_id, prediction.team1)}
            disabled={!!userPick}
          />

          <div className="text-center">
            <span className="text-2xl font-bold text-slate-400">VS</span>
            {userPick && (
              <div className="mt-2 text-xs text-green-600 font-bold">
                Your Pick: {userPick}
              </div>
            )}
          </div>

          <TeamOption 
            team={prediction.team2}
            logo={prediction.team2_logo}
            isPicked={userPick === prediction.team2}
            pickCount={team2Picks}
            totalPicks={totalPicks}
            onPick={() => onPick(prediction.prediction_id, prediction.team2)}
            disabled={!!userPick}
          />
        </div>

        {totalPicks > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200 text-center text-sm text-slate-600">
            {totalPicks} {totalPicks === 1 ? 'pick' : 'picks'} made
          </div>
        )}
      </div>
    </div>
  );
};

const TeamOption = ({ team, logo, isPicked, pickCount, totalPicks, onPick, disabled }) => {
  const percentage = totalPicks > 0 ? Math.round((pickCount / totalPicks) * 100) : 0;

  return (
    <button
      onClick={onPick}
      disabled={disabled}
      className={`p-4 border-2 rounded-lg transition-all duration-200 ${
        isPicked 
          ? 'border-[#007AFF] bg-blue-50'
          : 'border-slate-200 hover:border-[#007AFF] hover:bg-slate-50'
      } ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
    >
      {logo && (
        <img src={logo} alt={team} className="h-16 w-16 mx-auto mb-2 object-contain" />
      )}
      <h4 className="text-lg font-bold uppercase heading-font text-[#001f3f] mb-1">{team}</h4>
      {disabled && totalPicks > 0 && (
        <div className="text-xs">
          <span className="font-bold text-[#007AFF]">{percentage}%</span>
        </div>
      )}
    </button>
  );
};

export default FanZonePage;
